import { useState, type FormEvent } from 'react'
import QRCode from 'qrcode'
import { useTranslation } from 'react-i18next'
import { Button, Card, Input } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { authApi } from '@/lib/auth-api'

type Phase = 'idle' | 'enrolling' | 'backup' | 'disabling'

export function TwoFactorSetup({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  const { t } = useTranslation('auth')
  const [phase, setPhase] = useState<Phase>('idle')
  const [secret, setSecret] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function startEnroll() {
    setError(null)
    setLoading(true)
    try {
      const r = await authApi.twoFactor.enroll()
      setSecret(r.secret)
      setQrDataUrl(await QRCode.toDataURL(r.otpauth_url))
      setPhase('enrolling')
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function verifyEnroll(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const r = await authApi.twoFactor.verify(code)
      setBackupCodes(r.backup_codes)
      setPhase('backup')
      setCode('')
      onChange()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function disable(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await authApi.twoFactor.disable(password)
      setPassword('')
      setPhase('idle')
      onChange()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function regenerate() {
    setError(null)
    setLoading(true)
    try {
      const r = await authApi.twoFactor.regenerateBackup()
      setBackupCodes(r.backup_codes)
      setPhase('backup')
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  if (phase === 'backup') {
    return (
      <Card>
        <h3 className="text-lg font-medium">{t('two_factor.backup_title')}</h3>
        <p className="mt-1 text-sm text-text-muted">{t('two_factor.backup_body')}</p>
        <ul className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm">
          {backupCodes.map((c) => (
            <li key={c} className="rounded-sm bg-bg-subtle px-2 py-1">
              {c}
            </li>
          ))}
        </ul>
        <Button className="mt-4" onClick={() => setPhase('idle')}>
          {t('two_factor.backup_done')}
        </Button>
      </Card>
    )
  }

  if (phase === 'enrolling') {
    return (
      <Card>
        <h3 className="text-lg font-medium">{t('two_factor.scan_title')}</h3>
        <p className="mt-1 text-sm text-text-muted">{t('two_factor.scan_hint')}</p>
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={t('two_factor.scan_title')}
            className="mt-4 h-44 w-44 rounded-md bg-white p-2"
          />
        ) : null}
        <p className="mt-3 text-xs text-text-subtle">{t('two_factor.manual_label')}</p>
        <p className="select-all font-mono text-sm">{secret}</p>
        <form className="mt-4 flex flex-col gap-3" onSubmit={verifyEnroll}>
          <Input
            label={t('two_factor.code_label')}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          {error ? <ErrorBanner message={error} /> : null}
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => setPhase('idle')}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {t('two_factor.verify')}
            </Button>
          </div>
        </form>
      </Card>
    )
  }

  if (phase === 'disabling') {
    return (
      <Card>
        <h3 className="text-lg font-medium">{t('two_factor.disable_title')}</h3>
        <form className="mt-4 flex flex-col gap-3" onSubmit={disable}>
          <Input
            label={t('two_factor.disable_password_label')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error ? <ErrorBanner message={error} /> : null}
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => setPhase('idle')}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {t('two_factor.disable_submit')}
            </Button>
          </div>
        </form>
      </Card>
    )
  }

  return (
    <Card>
      <p className="text-text-muted">
        {enabled ? t('two_factor.status_on') : t('two_factor.status_off')}
      </p>
      {error ? <div className="mt-3"><ErrorBanner message={error} /></div> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {enabled ? (
          <>
            <Button variant="secondary" onClick={regenerate} disabled={loading}>
              {t('two_factor.regenerate')}
            </Button>
            <Button variant="tertiary" onClick={() => setPhase('disabling')}>
              {t('two_factor.disable')}
            </Button>
          </>
        ) : (
          <Button onClick={startEnroll} disabled={loading}>
            {t('two_factor.enable')}
          </Button>
        )}
      </div>
    </Card>
  )
}
